import { useEffect, useState } from 'react'
import QRCode, { type QRCodeToStringOptions } from 'qrcode'
import clsx from 'clsx'

type QrCodeProps = {
    /** Text/URL encoded into the QR code. */
    value: string
    /** Rendered pixel size (width = height). */
    size?: number
    /** Foreground (modules) color. */
    color?: string
    /** Background color. */
    background?: string
    /** Quiet-zone width in modules. */
    margin?: number
    /** Error-correction level. */
    errorCorrectionLevel?: QRCodeToStringOptions['errorCorrectionLevel']
    className?: string
}

/**
 * Renders a QR code (crisp scalable SVG) for the given `value`.
 *
 * Generation is async via the `qrcode` library; nothing renders until the
 * code is ready (and an empty `value` renders nothing).
 */
export default function QrCode({
    value,
    size = 256,
    color = '#0E0622',
    background = '#FFFFFF',
    margin = 1,
    errorCorrectionLevel = 'M',
    className,
}: QrCodeProps) {
    const [svgMarkup, setSvgMarkup] = useState<string | null>(null)

    useEffect(() => {
        let cancelled = false
        const trimmed = value.trim()
        if (!trimmed) {
            setSvgMarkup(null)
            return
        }

        void QRCode.toString(trimmed, {
            type: 'svg',
            margin,
            errorCorrectionLevel,
            color: { dark: color, light: background },
        })
            .then((svg) => {
                if (!cancelled) {
                    setSvgMarkup(svg)
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setSvgMarkup(null)
                }
            })

        return () => {
            cancelled = true
        }
    }, [value, color, background, margin, errorCorrectionLevel])

    if (!svgMarkup) {
        return null
    }

    return (
        <div
            role='img'
            aria-label='QR-Code'
            className={clsx('[&>svg]:block [&>svg]:w-full [&>svg]:h-full', className)}
            style={{ width: size, height: size }}
            dangerouslySetInnerHTML={{ __html: svgMarkup }}
        />
    )
}
